import { useCallback, useRef, useState, useEffect } from "react";
import { IGif } from "@/common/services/types";
import { playbackTracker } from "../utils/playback";
import { useUserStore } from "@/common/store/userStore";
import { useAnalyticsStore } from "@/common/store/analyticsStore";

interface UseVideoPlayerProps {
  gif: IGif;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onSound: (sound: boolean, persistent?: boolean, showAnimation?: boolean) => void;
  sendGifView: (gif: IGif) => void;
  position?: number | null;
  playCount: number;
  incrementPlayCount: () => void;
}

const RETRY_DELAY = 1000;
const MS_IN_SEC = 1000;

export const usePlaybackControl = (props: UseVideoPlayerProps) => {
  const {
    gif,
    onSound,
    videoRef,
    sendGifView,
    position,
    playCount,
    incrementPlayCount
  } = props;
  const currentUser = useUserStore((state) => state.currentUser);
  const playPromiseRef = useRef<Promise<void>>(undefined);
  const [isTriedToPlay, setIsTriedToPlay] = useState<boolean>(false);
  const [paused, setPaused] = useState<boolean>(false);
  const eventSource = useAnalyticsStore((state) => state.eventSource);

  const startPlayback = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    const startPointMs = Math.floor(videoRef.current.currentTime * MS_IN_SEC);
    playbackTracker.start(gif, eventSource, currentUser?.username, position, startPointMs, playCount);
  }, [videoRef, gif, currentUser, position, eventSource, playCount]);

  const handlePlayError = useCallback((e: Error) => {
    console.warn("failed to play video", e);

    if (!videoRef.current) {
      return;
    }

    if (videoRef.current.muted) {
      setPaused(true);
      setIsTriedToPlay(true);
      playbackTracker.stop();
      return;
    }

    if (e.name === "NotAllowedError") {
      onSound(false, false, false);
    }

    setTimeout(() => {
      if (!videoRef.current) {
        return;
      }

      playPromiseRef.current = videoRef.current.play();
      playPromiseRef.current.then(() => {
        setPaused(false);
        sendGifView(gif);
        incrementPlayCount();
        startPlayback();
      }).catch(e => {
        console.error("Failed to play video on retry", e);
        setPaused(true);
        setIsTriedToPlay(true);
        playbackTracker.stop();
      });
    }, RETRY_DELAY);
  }, [gif, sendGifView, videoRef, onSound, startPlayback]);

  const play = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    try {
      playPromiseRef.current = videoRef.current.play();

      if (playPromiseRef.current !== undefined) {
        playPromiseRef.current.then(() => {
          setPaused(false);
          sendGifView(gif);
          incrementPlayCount();
          startPlayback();
        }).catch(handlePlayError).finally(() => {
          setIsTriedToPlay(true);
        });
      } else {
        setPaused(false);
        setIsTriedToPlay(true);
        sendGifView(gif);
        incrementPlayCount();
        startPlayback();
      }
    } catch (e) {
      handlePlayError(e as Error);
    }
  }, [sendGifView, gif, videoRef, handlePlayError, startPlayback, incrementPlayCount]);

  const pause = useCallback(() => {
    if (playPromiseRef.current !== undefined) {
      playPromiseRef.current.then(() => {
        if (!videoRef.current) {
          return;
        }

        playbackTracker.stop();
        videoRef.current.pause();
        setPaused(true);
      });
    } else {
      if (!videoRef.current) {
        return;
      }

      playbackTracker.stop();
      videoRef.current.pause();
      setPaused(true);
    }
  }, [videoRef]);

  useEffect(() => {
    if (playCount) {
      playbackTracker.setPlayCount(playCount);
    }
  }, [playCount]);

  useEffect(() => {
    return () => {
      playbackTracker.stop(gif.id);
    };
  }, []);

  return {
    play,
    pause,
    paused,
    setPaused,
    isTriedToPlay,
    startPlayback
  };
};
